import React, { useCallback, useEffect, useState } from 'react'
import { Branch, FinanceCourierQuery } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import EditFinanceCourierForm, {
  EditFinanceCourierErrorType,
  EditFinanceCourierFormType,
  initialError,
  initialForm
} from '../../../components/admin/finance/EditFinanceCourierForm'
import useT from '../../../i18ns/admin/useT'
import { useDialogHandleSubmit, useRedirectHandleBack } from '../../../utils/default/ComplexFlowHook'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { Path } from '../../../components/admin/route/route'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'

const formToRequest: (form: EditFinanceCourierFormType) => FinanceCourierQuery = (form) => {
  return {
    slug: form.slug,
    name: form.name,
    secret: form.secret,
    is_active: form.is_active
  }
}

const defaultForm = initialForm()

const FinanceCourierCreatePage: React.FC = () => {
  const classes = useCommonStyles()
  const globalDialog = useGlobalDialog()
  const gdk = useGDK()
  const { t } = useT()
  const forwarders = useGDKStore.finance.forwarders()

  useEffect(() => {
    gdk.trunk.trigger([Branch.FORWARDERS])
  }, [gdk])

  const [formError] = useState<EditFinanceCourierErrorType>(initialError())
  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({
    path: Path.FINANCE_COURIER
  })

  const { handleSubmit } = useDialogHandleSubmit<EditFinanceCourierFormType, FinanceCourierQuery, null>({
    dialogId: 'createCourier',
    globalDialog,
    getChangeDialogConfig: useCallback((form) => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmCreateCourrier')}
          <span className={classes.purpleWord}>{`${forwarders[form.slug]?.name ?? ''} ${form.name}`}</span>
        </React.Fragment>
      )
    }), [t, forwarders, classes.purpleWord]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.createCourierSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    formToRequest,
    gdkFunc: (payload) => gdk.finance.createCourier(payload),
    gdkFuncDependencies: [gdk],
    afterSuccessDialog: handleBack
  })

  return (
    <Box paddingY={5}>
      <EditFinanceCourierForm
        title={t('page.createCourier')}
        okText={t('common.confirmCreate')}
        onBack={handleDebouncedBack}
        onSubmit={handleSubmit}
        defaultValue={defaultForm}
        formError={formError}
        requireSecret
      />
    </Box>
  )
}

export default React.memo(FinanceCourierCreatePage)
